"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GratisOfferte() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section ref={ref} className="w-full bg-dark-background py-24 px-4">
      <div className={`max-w-2xl mx-auto transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-foreground-dark mb-4">
            Gratis offerte
          </h2>
          <p className="text-lg text-foreground-dark/90">
            Vraag nu een vrijblijvende offerte aan en ontvang binnen 24 uur een reactie van onze experts.
          </p>
        </div>

        <FormProvider className="w-full" formId="gratis-offerte-form" sectionName="gratis-offerte" defaultValues={{
        name: "",
        email: "",
        phone: "",
        address: ""
      }} onSubmit={async data => {
        try {
          await wvcClient.formSubmission({
            sectionName: "gratis-offerte",
            formId: "gratis-offerte-form",
            formData: data
          });
        } catch (error) {
          console.error("Form submission error:", error);
          throw error;
        }
      }} submitText="Offerte aanvragen" successMessage="Bedankt! We nemen binnen 24 uur contact met u op." errorMessage="Er is iets misgegaan. Probeer het opnieuw.">
          <QuoteForm isVisible={isVisible} />
        </FormProvider>
      </div>
    </section>;
}
function QuoteForm({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control,
    formState: {
      errors
    }
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className={`p-6 bg-secondary/20 border border-secondary rounded-md transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <p className="text-foreground-dark text-center mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full bg-background/10 text-foreground-dark border-foreground-dark/30 hover:bg-background/20">
          Nieuwe offerte aanvragen
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht",
        minLength: {
          value: 2,
          message: "Minimaal 2 tekens"
        },
        maxLength: {
          value: 100,
          message: "Maximale 100 tekens"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "40ms"
      }}>
              <FormLabel className="text-foreground-dark">Naam</FormLabel>
              <FormControl>
                <Input placeholder="Uw volledige naam" {...field} className="bg-background/10 border-foreground-dark/30 text-foreground-dark placeholder:text-foreground-dark/50 focus-visible:border-primary focus-visible:ring-primary/40" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-mail is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "80ms"
      }}>
              <FormLabel className="text-foreground-dark">E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="u@voorbeeld.be" {...field} className="bg-background/10 border-foreground-dark/30 text-foreground-dark placeholder:text-foreground-dark/50 focus-visible:border-primary focus-visible:ring-primary/40" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Telefoonnummer is verplicht",
        minLength: {
          value: 10,
          message: "Minimaal 10 tekens"
        },
        maxLength: {
          value: 20,
          message: "Maximaal 20 tekens"
        }
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "120ms"
      }}>
              <FormLabel className="text-foreground-dark">Telefoonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+32 2 123 45 67" {...field} className="bg-background/10 border-foreground-dark/30 text-foreground-dark placeholder:text-foreground-dark/50 focus-visible:border-primary focus-visible:ring-primary/40" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="address" rules={{
        required: "Adres is verplicht"
      }} render={({
        field
      }) => <FormItem className={`transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "160ms"
      }}>
              <FormLabel className="text-foreground-dark">Adres en type keuring</FormLabel>
              <FormControl>
                <Input placeholder="Straatnaam, huisnummer, postcode, stad" {...field} className="bg-background/10 border-foreground-dark/30 text-foreground-dark placeholder:text-foreground-dark/50 focus-visible:border-primary focus-visible:ring-primary/40" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className={`p-4 bg-red-500/10 border border-red-500/30 rounded-md transition-all duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "200ms"
      }}>
            <p className="text-red-300 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className={`w-full bg-primary text-foreground-primary hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 transition-all duration-150 ease-out focus-visible:ring-primary/40 ${isSubmitting ? "animate-pulse" : ""} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "200ms"
      }}>
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Verzenden...
            </> : "Offerte aanvragen"}
        </Button>
      </div>
    </Form>;
}